import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { Mail, Phone, MapPin, ArrowRight, Instagram, Facebook } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const { settings } = useSiteSettings();

  async function onSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-[80vh]">
      {/* ── Page header ───────────────────────────────────────────────── */}
      <div className="container-x pt-16 md:pt-24 pb-12 md:pb-16">
        <div className="max-w-xl">
          <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
            Get in touch
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-light leading-[1.05]">
            Let&apos;s{" "}
            <span className="italic text-[hsl(var(--gold))]">talk.</span>
          </h1>
          <p className="mt-5 text-base text-foreground/65 leading-relaxed max-w-lg">
            Tell us about your project, your dates, and what you&apos;d like to create.
            We respond within one business day.
          </p>
        </div>
      </div>

      {/* ── Thin gold divider ────────────────────────────────────────── */}
      <div className="h-px bg-gradient-to-r from-[hsl(var(--gold))]/60 via-[hsl(var(--gold))]/20 to-transparent mx-6 md:mx-12 lg:mx-24" />

      {/* ── Main content ─────────────────────────────────────────────── */}
      <div className="container-x py-14 md:py-20 grid md:grid-cols-12 gap-12 md:gap-16">
        {/* Info panel */}
        <div className="md:col-span-4">
          <div className="space-y-8">
            {[
              {
                icon: Mail,
                label: "Email",
                value: settings?.contact_email || "hello@allureimage.com",
                href: `mailto:${settings?.contact_email || "hello@allureimage.com"}`,
              },
              {
                icon: Phone,
                label: "Phone",
                value: settings?.contact_phone || "+1 (555) 010-2026",
                href: `tel:${settings?.contact_phone || "+15550102026"}`,
              },
              {
                icon: MapPin,
                label: "Location",
                value: settings?.contact_location || "By appointment — New Jersey",
                href: null,
              },
            ].map(({ icon: Icon, label, value, href }) => (
              <div key={label} className="flex items-start gap-4">
                <div className="w-9 h-9 border border-border flex items-center justify-center shrink-0 mt-0.5">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
                    {label}
                  </div>
                  {href ? (
                    <a
                      href={href}
                      className="text-sm hover:text-[hsl(var(--gold))] transition-colors"
                    >
                      {value}
                    </a>
                  ) : (
                    <p className="text-sm">{value}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Hours */}
          <div className="mt-10 pt-8 border-t border-border">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
              Studio hours
            </div>
            <div className="space-y-2 text-sm text-foreground/70">
              <div className="flex justify-between">
                <span>Monday – Friday</span>
                <span>10:00 – 18:00</span>
              </div>
              <div className="flex justify-between">
                <span>Saturday</span>
                <span>10:00 – 15:00</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Sunday</span>
                <span>Closed</span>
              </div>
            </div>
          </div>

          {/* Social links */}
          <div className="mt-10 pt-8 border-t border-border">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-4">
              Follow us
            </div>
            <div className="flex gap-3">
              {settings?.instagram_url && (
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-9 h-9 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              )}
              {settings?.facebook_url && (
                <a
                  href={settings.facebook_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-9 h-9 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
                >
                  <Facebook className="w-4 h-4" />
                </a>
              )}
              <a
                href={`mailto:${settings?.contact_email || "hello@allureimage.com"}`}
                aria-label="Email"
                className="w-9 h-9 border border-border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="md:col-span-8"
          data-testid="contact-form"
        >
          <div className="grid sm:grid-cols-2 gap-6 md:gap-8">
            <Field label="Name" required>
              <input
                data-testid="contact-name"
                required
                placeholder="Jane Smith"
                className="w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground placeholder:text-muted-foreground/40 transition-colors text-sm"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Field>
            <Field label="Email" required>
              <input
                data-testid="contact-email"
                type="email"
                required
                placeholder="jane@example.com"
                className="w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground placeholder:text-muted-foreground/40 transition-colors text-sm"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Phone">
              <input
                data-testid="contact-phone"
                placeholder="+1 (555) 000-0000"
                className="w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground placeholder:text-muted-foreground/40 transition-colors text-sm"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </Field>
            <Field label="Session type">
              <input
                placeholder="e.g. Maternity, Glamour…"
                className="w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground placeholder:text-muted-foreground/40 transition-colors text-sm"
                disabled
                aria-hidden="true"
              />
            </Field>
          </div>

          <div className="mt-6 md:mt-8">
            <Field label="Message" required>
              <textarea
                data-testid="contact-message"
                required
                rows={6}
                placeholder="Tell us about your shoot — vision, date ideas, anything important…"
                className="w-full bg-transparent border-b border-border py-3 outline-none focus:border-foreground placeholder:text-muted-foreground/40 resize-none transition-colors text-sm leading-relaxed"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
              />
            </Field>
          </div>

          <div className="mt-8 flex items-center gap-6">
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="inline-flex items-center gap-3 px-7 h-12 bg-primary text-primary-foreground text-xs uppercase tracking-[0.22em] hover:bg-primary/90 disabled:opacity-50 transition-colors group"
            >
              {loading ? "Sending…" : "Send message"}
              {!loading && (
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              )}
            </button>
            <p className="text-xs text-muted-foreground">
              We reply within 1 business day.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
        {label}
        {required && <span className="text-foreground ml-0.5">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
